import { useCallback, useEffect, useState } from 'react';
import { collection, getDocs, orderBy, query, Timestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { firestore } from '../lib/firebase';
import type { LeadPayload } from '../services/leads';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { LeadRow } from '../components/admin/LeadRow';
import { LeadAnalytics } from '../components/admin/LeadAnalytics';
import { MediaManager } from '../components/admin/MediaManager';
import { ContentManager } from '../components/admin/ContentManager';

type LeadWithMeta = LeadPayload & { id: string; createdAt?: Date };

export function AdminDashboard() {
  const navigate = useNavigate();
  const { logout } = useAdminAuth();
  const [leads, setLeads] = useState<LeadWithMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [view, setView] = useState<'analytics' | 'table' | 'media' | 'content'>('analytics');

  const loadLeads = useCallback(async () => {
    try {
      setLoading(true);
      const leadsRef = collection(firestore, 'default');
      const snapshot = await getDocs(query(leadsRef, orderBy('createdAt', 'desc')));
      const data: LeadWithMeta[] = snapshot.docs.map((doc) => {
        const payload = doc.data() as LeadPayload & { createdAt?: Timestamp; processedAt?: Timestamp };
        const createdAt = payload.createdAt ? payload.createdAt.toDate() : undefined;
        const processedAt = payload.processedAt ? payload.processedAt.toDate() : undefined;

        return {
          id: doc.id,
          ...payload,
          createdAt,
          processedAt,
        };
      });
      setLeads(data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadLeads();
  }, [loadLeads]);


  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 p-8">
      <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Pannello Lead</h1>
          <p className="text-slate-400">Gestione interna delle richieste e dei contenuti marketing dinamici.</p>
        </div>
        <div className="flex flex-col gap-3 md:items-end">
          <div className="inline-flex rounded-xl border border-slate-800 bg-slate-900/60 p-1">
            {(
              [
                { id: 'analytics', label: '📊 Analytics' },
                { id: 'table', label: '📋 Tabella' },
                { id: 'media', label: '🎬 Media' },
                { id: 'content', label: '📝 Testi' },
              ] as const
            ).map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setView(option.id)}
                className={`px-4 py-2 text-sm font-medium transition rounded-lg ${
                  view === option.id
                    ? 'bg-indigo-700 text-white shadow'
                    : 'text-slate-300 hover:text-white hover:bg-slate-800'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => {
              void logout().then(() => {
                navigate('/internal/regai-admin/login', { replace: true });
              });
            }}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition text-sm"
          >
            Esci
          </button>
        </div>
      </div>

      {loading && <p className="text-slate-300">Caricamento lead…</p>}
      {error && (
        <div className="bg-red-900/40 border border-red-500 text-red-200 rounded-lg p-4 mb-6">
          Errore durante il caricamento: {error.message}
        </div>
      )}

      {!loading && !error && (
        <div className="space-y-8">
          {view === 'analytics' && <LeadAnalytics leads={leads} />}
          {view === 'table' && (
            <section>
              <h2 className="text-xl font-semibold mb-3">Dettaglio lead</h2>
              <div className="overflow-x-auto bg-slate-900 border border-slate-800 rounded-lg">
                <table className="min-w-full divide-y divide-slate-800">
                  <thead className="bg-slate-900/80">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                        Data
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                        Contatto
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                        Ruolo
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                        Scuola
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                        Interessi
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                        Messaggio
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                        Stato
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                        Azioni
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {leads.map((lead) => (
                      <LeadRow key={lead.id} lead={lead} onUpdate={loadLeads} />
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
          {view === 'media' && (
            <section>
              <h2 className="text-xl font-semibold mb-3">Media marketing</h2>
              <MediaManager />
            </section>
          )}
          {view === 'content' && (
            <section>
              <h2 className="text-xl font-semibold mb-3">Contenuti testuali</h2>
              <ContentManager />
            </section>
          )}
        </div>
      )}
    </div>
  );
}
